import React from 'react';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import { PcrSelectorRecord } from 'types/dynamic-forms/records/PcrSelectorRecord';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import usePcrSelection from '../hooks/usePcrSelection';
import { useDigitalVersionSwitch } from '../hooks/useDigitalVersionSwitch';

// The c-PCR select: a change, including clearing it, moves the document to the digital version of the selected
// PCR and the new c-PCR through the same lookup, confirmation and switch as the PCR. Hidden while the selected
// PCR has no c-PCRs.
const CpcrSelectorRenderer: React.FC<PcrSelectorRecord> = ({ label, tooltip, helpText }) => {
  const { switchDigitalVersion } = useDigitalVersionSwitch();
  const { selection, cpcrOptions, isDisabled } = usePcrSelection();

  if (!selection.pcrId || cpcrOptions.length === 0) {
    return null;
  }
  const pcrId = selection.pcrId;

  const handleChange = async (nextValue: string | null) => {
    // Re-selecting the current value must not resolve the digital version again.
    if (nextValue === selection.cpcrId) {
      return;
    }
    const selectedOption = nextValue ? cpcrOptions.find((o) => o.value === nextValue) : undefined;
    if (nextValue && (!selectedOption || selectedOption.isDisabled)) {
      return;
    }

    await switchDigitalVersion({
      selection: { pcrId, cpcrId: nextValue },
      confirmation: {
        caption: 'Confirm c-PCR change',
        description: (
          <>
            {selectedOption ? (
              <p>The selected c-PCR "{selectedOption.label}" changes the custom data of the form.</p>
            ) : (
              <p>Removing the c-PCR changes the custom data of the form.</p>
            )}
            <p>The fields on the form will be changed which can lead to losing already added data.</p>
            <p>Please press "Confirm" to proceed or press "Cancel" to roll back the c-PCR selection.</p>
          </>
        ),
      },
    });
  };

  return (
    <WizardSelectControlledComponent
      name="cpcrSelector"
      options={cpcrOptions}
      value={cpcrOptions.find((o) => o.value === selection.cpcrId) ?? null}
      label={label}
      tooltip={tooltip}
      helpText={helpText}
      onChanged={(_: string, newValue: any) => handleChange(newValue ?? null)}
      disabled={isDisabled}
      {...reactSelectPropsForHtmlLabel}
    />
  );
};

export default CpcrSelectorRenderer;
